













































import { Component, Mixins, Prop } from 'vue-property-decorator'
import OzBox, { OzBoxColors } from 'library/v4/components/OzBox.vue'
import OzBaseDialogBox, {
  OzBaseDialogBoxButtonData,
  OzContainedButtonColorScheme,
} from 'library/v4/components/OzBaseDialogBox.vue'
import OzInput from 'library/v4/components/OzInput.vue'
import { __ } from 'bits/intl'
import isChromatic from 'chromatic/isChromatic'
// Fix this for Chromatic comparison
const generateCode = (): string =>
  isChromatic()
    ? '0539'
    : Math.floor(Math.random() * 10000)
        .toString()
        .padStart(4, '0')

enum ButtonScheme {
  Default = 'Default',
  Danger = 'Danger',
  PromoteCancellation = 'PromoteCancellation',
}

@Component({ components: { OzBox, OzBaseDialogBox, OzInput } })
export default class OzConfirmationDialogBox extends Mixins() {
  OzBoxColors = OzBoxColors

  codeConfirmationText = __('Enter this code to proceed:')
  confirmationCode = generateCode()
  enteredCode = ''

  $refs!: {
    confirmationCodeInput: OzInput
  }

  /**
   * Dark mode.
   */
  @Prop({ default: 'auto' }) darkMode!: true | false | 'auto'

  /**
   * Icon.
   */
  @Prop({ default: null }) iconSrc!: string | null

  /**
   * Icon alt text.
   */
  @Prop({ default: 'icon' }) iconAlt!: string

  /**
   * Title.
   */
  @Prop({ required: true }) title!: string

  /**
   * Body.
   */
  @Prop() body?: string

  /**
   * Confirm with a confirmation code.
   */
  @Prop({ default: false }) isCodeProtected!: boolean

  /**
   * Confirm button text.
   */
  @Prop() confirmButtonText?: string

  /**
   * Cancel button text.
   */
  @Prop() cancelButtonText?: string

  /**
   * Pick a button scheme based on the intended use case:
   *
   * 1. `Default` - `confirmButton` is highlighted as the primary action.
   * 1. `Danger` - `confirmButton` is highlighted in red to signal danger.
   * 1. `PromoteCancellation` - `cancelButton` is positioned as the primary action. `confirmButton`'s color is muted.
   */
  @Prop({ default: ButtonScheme.Default }) buttonScheme?: ButtonScheme

  /**
   * Determines if each button should fill the width of the dialog
   */
  @Prop({ default: false }) forceFullWidthButtons!: boolean

  get buttons(): OzBaseDialogBoxButtonData[] {
    /**
     * Emits events passed in via the `buttons` prop.
     * @event cancel
     */
    const cancelButton = {
      text: this.cancelButtonText || __('Cancel'),
      emit: 'cancel',
      colorScheme: OzContainedButtonColorScheme.Secondary,
      testId: 'cancelButton',
    }
    /**
     * Emits events passed in via the `buttons` prop.
     * @event confirm
     */
    const confirmButton = {
      text: this.confirmButtonText || __('Continue'),
      emit: 'confirm',
      colorScheme: OzContainedButtonColorScheme.Primary,
      disabled: this.isCodeProtected && !this.enteredCode.trim(),
      testId: 'confirmButton',
    }

    // When the buttons are in a row, the prioritized action is right-most.
    // When the buttons are stacked, the prioritized action is top-most.
    let buttonList: OzBaseDialogBoxButtonData[]

    if (this.buttonScheme === ButtonScheme.Default) {
      buttonList = [cancelButton, confirmButton]
    } else if (this.buttonScheme === ButtonScheme.Danger) {
      buttonList = [cancelButton, { ...confirmButton, colorScheme: OzContainedButtonColorScheme.Destructive }]
    } else if (this.buttonScheme === ButtonScheme.PromoteCancellation) {
      buttonList = [{ ...confirmButton, colorScheme: OzContainedButtonColorScheme.SecondaryClear }, cancelButton]
    } else {
      buttonList = [cancelButton, confirmButton]
    }

    return this.forceFullWidthButtons ? buttonList.reverse() : buttonList
  }

  focus(): void {
    this.$nextTick(() => this.$refs?.confirmationCodeInput?.focusInput())
  }

  cancel(): void {
    /**
     * Cancel.
     */
    this.$emit('cancel')
  }

  confirm(): void {
    /**
     * Action confirmed.
     */
    this.$emit('confirm')
  }

  submit(): void {
    if (!this.isCodeProtected) {
      this.confirm()
    } else if (this.enteredCode.trim() === this.confirmationCode) {
      this.confirm()
    } else {
      this.confirmationCode = generateCode()
    }
    this.enteredCode = ''
  }
}

export { ButtonScheme as OzConfirmationDialogBoxButtonScheme }
